import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  productionDiscardApi,
  type CreateProductionDiscardDto,
  type ApproveProductionDiscardDto,
} from "../services/productionDiscardApi";

export const useProductionDiscardPendingApprovals = (
  pageNumber: number,
  pageSize: number,
  search: string,
  source: "Greenhouse" | "Agroforestry"
) =>
  useQuery({
    queryKey: ["production-discard", "pending", source, pageNumber, pageSize, search],
    queryFn: () => productionDiscardApi.getPendingApprovals(pageNumber, pageSize, search, source),
    placeholderData: (previous) => previous,
  });

export const useProductionDiscardBySowingInstruction = (sowingInstructionId: string) =>
  useQuery({
    queryKey: ["production-discard", "by-sowing", sowingInstructionId],
    queryFn: () => productionDiscardApi.getBySowingInstruction(sowingInstructionId),
    enabled: !!sowingInstructionId,
  });

export const useGreenhouseDiscardAvailability = (sowingInstructionId: string, enabled: boolean) =>
  useQuery({
    queryKey: ["production-discard", "availability", sowingInstructionId],
    queryFn: () => productionDiscardApi.getAvailability(sowingInstructionId),
    enabled: enabled && !!sowingInstructionId,
  });

export const useCreateProductionDiscard = () =>
  useMutation({
    mutationFn: (dto: CreateProductionDiscardDto) => productionDiscardApi.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-discard"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock"] });
    },
  });

export const useApproveProductionDiscard = () =>
  useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ApproveProductionDiscardDto }) =>
      productionDiscardApi.approve(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-discard"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock"] });
      queryClient.invalidateQueries({ queryKey: ["agroforestry-stock"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      queryClient.invalidateQueries({ queryKey: ["sowingInstructions"] });
    },
  });

export const useProcessProductionDiscard = () =>
  useMutation({
    mutationFn: (id: string) => productionDiscardApi.process(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["production-discard"] });
      queryClient.invalidateQueries({ queryKey: ["greenhouse-stock"] });
    },
  });
